import React, { useState } from 'react';

import type { GameImportDto } from '../../api/types';
import { gamesService } from '../../store/thunks/_deps';

import './ImportGameModal.css';

type ImportSource = 'tesera';

interface ImportGameModalProps {
  open: boolean;
  onClose: () => void;
  onImported: (data: GameImportDto) => void;
}

function extractTeseraAlias(url: string): string | null {
  const trimmed = url.trim();

  const htmlMatch = trimmed.match(/tesera\.ru\/game\/([A-Za-z0-9_-]+)/);
  if (htmlMatch) return htmlMatch[1];

  const apiMatch = trimmed.match(/api\.tesera\.ru\/games\/([A-Za-z0-9_-]+)/);
  if (apiMatch) return apiMatch[1];

  if (/^[A-Za-z0-9_-]+$/.test(trimmed)) return trimmed;

  return null;
}

export function ImportGameModal({ open, onClose, onImported }: ImportGameModalProps) {
  const [source, setSource] = useState<ImportSource | null>(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setSource(null);
    setUrl('');
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleImport = async () => {
    if (!source || !url.trim()) return;

    const alias = extractTeseraAlias(url);
    if (!alias) {
      setError('Не удалось определить alias игры из ссылки. Пример: https://tesera.ru/game/Star-Wars-Rebellion');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await gamesService.importGame({ source, identifier: alias });
      onImported(data);
      handleClose();
    } catch {
      setError('Не удалось загрузить данные. Проверьте ссылку и попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`modal-overlay${open ? ' show' : ''}`} onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          &times;
        </button>

        <div className="modal-header">
          <div className="modal-title">Импорт игры</div>
          <div className="modal-subtitle">Загрузить данные с внешнего ресурса</div>
        </div>

        <div className="import-modal__body">
          <div>
            <div className="import-modal__step-label">1. Выберите источник</div>
            <div className="import-modal__source-select">
              <button
                className={`import-modal__source-btn${source === 'tesera' ? ' import-modal__source-btn--active' : ''}`}
                onClick={() => { setSource('tesera'); setError(null); }}
              >
                tesera.ru
              </button>
            </div>
          </div>

          {source === 'tesera' && (
            <div>
              <div className="import-modal__step-label">2. Вставьте ссылку на игру</div>
              <div className="import-modal__url-group">
                <input
                  className="import-modal__url-input"
                  type="text"
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setError(null); }}
                  placeholder="https://tesera.ru/game/Star-Wars-Rebellion"
                  disabled={loading}
                  autoFocus
                />
                <span className="import-modal__url-hint">
                  Ссылка на страницу игры на tesera.ru или alias (например, Star-Wars-Rebellion)
                </span>
              </div>
            </div>
          )}

          {error && <div className="import-modal__error">{error}</div>}

          {loading && (
            <div className="import-modal__loading">
              <div className="import-modal__spinner" />
              Загрузка данных...
            </div>
          )}

          {source && !loading && (
            <div className="import-modal__actions">
              <button className="import-modal__cancel" onClick={handleClose}>
                Отмена
              </button>
              <button
                className="import-modal__submit"
                onClick={handleImport}
                disabled={!url.trim()}
              >
                Импортировать
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
